import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { MaskPipe } from 'ngx-mask';
import Utils from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ApprovalService } from 'src/app/services/approval/approval.service';

@Component({
  selector: 'app-dashboard-approval-composition',
  templateUrl: './dashboard-approval-composition.component.html',
  styleUrls: ['./dashboard-approval-composition.component.scss'],
})
export class DashboardApprovalCompositionComponent
  implements OnInit, OnChanges, OnDestroy
{
  @ViewChild('modalRoot') modalRoot;
  @Input() comparingVersions: boolean = false;

  // Dados para versão principal
  mainData: PartnerResponse[] = [];
  mainLoading = false;

  // Dados para versão comparando
  comparingData: PartnerResponse[] = [];
  comparingLoading = false;
  comparingDataLoading = false;

  // Dados normais (sem comparação)
  data: PartnerResponse[] = [];
  loading = true;

  compositionDetails = false;
  selectedPartner: Partner = null;

  showModal = false;
  file: Files = null;

  isPDF = false;
  url: any;
  isXLS = false;

  eventMainVersion: any;
  eventComparingVersion: any;
  eventVersion: any;

  constructor(
    private sanitizer: DomSanitizer,
    private api: ApiService,
    private route: ActivatedRoute,
    private authService: AuthService,
    public approvalService: ApprovalService,
    private cdr: ChangeDetectorRef,
    private maskPipe: MaskPipe
  ) {}

  ngOnInit(): void {
    if (this.comparingVersions) {
      this.loadComparingVersions();
    } else {
      this.initializeData();
    }

    // Listener para mudanças de versão no modo "visualizar todas"
    this.eventVersion = this.approvalService.changeVersion.subscribe(() => {
      if (!this.comparingVersions) {
        // Incrementar contador de loading v3 quando iniciar requisição
        this.approvalService.incrementV3Loading();
        // Ativar loading em todas as abas quando trocar de versão
        this.loading = true;
        this.getApprovalData();
      }
    });

    // Subscrever aos eventos de mudança de versão
    this.eventMainVersion = this.approvalService.changeMainVersion.subscribe(
      () => {
        if (this.comparingVersions) {
          this.loadMainVersion();
        }
      }
    );

    this.eventComparingVersion =
      this.approvalService.changeComparingVersion.subscribe(() => {
        if (this.comparingVersions) {
          this.loadComparingVersion();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detectar quando comparingVersions muda
    if (
      changes['comparingVersions'] &&
      !changes['comparingVersions'].firstChange
    ) {
      const previousValue = changes['comparingVersions'].previousValue;
      const currentValue = changes['comparingVersions'].currentValue;

      // Se mudou de true para false, restaurar dados originais do cache
      if (previousValue === true && currentValue === false) {
        this.restoreOriginalData();
      } else if (previousValue === false && currentValue === true) {
        // Se mudou de false para true, carregar dados de comparação
        this.loadComparingVersions();
      }
    }
  }

  private restoreOriginalData() {
    // Não restaurar se estiver em modo de comparação
    if (this.comparingVersions) {
      return;
    }

    // Verificar se há dados em cache
    if (this.approvalService.approvalData.composition) {
      const cachedData = this.approvalService.approvalData.composition;

      // Restaurar dados do cache
      if (cachedData.register && cachedData.register.partners) {
        this.formatData(cachedData.register.partners);
      }

      // Desativar loading
      this.loading = false;
      this.cdr.detectChanges();
    } else {
      // Se não houver cache, carregar dados normalmente
      this.initializeData();
    }
  }

  initializeData(): void {
    if (this.comparingVersions) {
      this.loadComparingVersions();
    } else {
      if (this.approvalService.approvalData.composition) {
        this.formatData(
          this.approvalService.approvalData.composition.register.partners
        );
        this.loading = false;
      } else {
        this.getApprovalData();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.eventMainVersion) {
      this.eventMainVersion.unsubscribe();
    }
    if (this.eventComparingVersion) {
      this.eventComparingVersion.unsubscribe();
    }
    if (this.eventVersion) {
      this.eventVersion.unsubscribe();
    }
  }

  loadComparingVersions() {
    // Só carregar se houver versões selecionadas
    if (this.approvalService.approvalData.mainRevision) {
      this.loadMainVersion();
    }
    if (this.approvalService.approvalData.comparingRevision) {
      this.loadComparingVersion();
    }
  }

  loadMainVersion() {
    if (!this.approvalService.approvalData.mainRevision) {
      this.mainData = [];
      this.mainLoading = false;
      return;
    }
    this.mainLoading = true;
    this.cdr.detectChanges();
    this.getApprovalData('main');
  }

  loadComparingVersion() {
    if (!this.approvalService.approvalData.comparingRevision) {
      this.comparingData = [];
      this.comparingLoading = false;
      this.comparingDataLoading = false;
      return;
    }
    this.comparingLoading = true;
    this.comparingDataLoading = true;
    this.cdr.detectChanges();
    this.getApprovalData('comparing');
  }

  async getApprovalData(versionType: 'main' | 'comparing' | null = null) {
    // Se estiver em modo de comparação e não tiver versionType, não fazer nada
    if (this.comparingVersions && !versionType) {
      return;
    }

    // Configurar loading
    if (versionType === 'main') {
      this.mainLoading = true;
      this.cdr.detectChanges();
    } else if (versionType === 'comparing') {
      this.comparingLoading = true;
      this.comparingDataLoading = true;
      this.cdr.detectChanges();
    } else {
      this.loading = true;
    }

    try {
      const register_uuid = this.route.snapshot.params.id;

      if (!register_uuid) {
        throw new Error('Register UUID não encontrado na rota');
      }

      const params: any = {
        data_type: 'partners',
      };

      // Determinar qual versão buscar
      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        params.version = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        params.version = this.approvalService.approvalData.comparingVersion;
      } else if (!versionType && this.approvalService.approvalData.version) {
        params.version = this.approvalService.approvalData.version;
      } else {
        params.last = true;
      }

      // Se estiver em modo de comparação e não houver versão selecionada, não fazer requisição
      if (
        this.comparingVersions &&
        versionType &&
        ((versionType === 'main' &&
          !this.approvalService.approvalData.mainVersion) ||
          (versionType === 'comparing' &&
            !this.approvalService.approvalData.comparingVersion))
      ) {
        if (versionType === 'main') {
          this.mainData = [];
          this.mainLoading = false;
          this.cdr.detectChanges();
        } else if (versionType === 'comparing') {
          this.comparingData = [];
          this.comparingLoading = false;
          this.comparingDataLoading = false;
          this.cdr.detectChanges();
        }
        return;
      }

      const response = await this.api.get<any>({
        route: `api/v3/registration/versions/register/${register_uuid}/`,
        params: params,
        token: true,
      });

      if (!response || !response.data || !response.data.versions?.partners) {
        throw new Error('Resposta da API inválida');
      }

      // Extrair dados da versão
      const versions = response.data.versions.partners.versions || [];

      if (versions.length === 0) {
        if (versionType === 'main') {
          this.mainData = [];
          this.mainLoading = false;
          this.cdr.detectChanges();
          return;
        } else if (versionType === 'comparing') {
          this.comparingData = [];
          this.comparingLoading = false;
          this.comparingDataLoading = false;
          this.cdr.detectChanges();
          return;
        } else {
          this.data = [];
          this.loading = false;
          this.cdr.detectChanges();
          return;
        }
      }

      const versionData = versions[0];
      const partnersData = versionData?.data || [];

      // Processar dados diretamente da API v3
      // Estrutura: response.data.versions.partners.versions[0].data[]
      if (versionType === 'main') {
        this.formatData(partnersData, 'main');
        this.mainLoading = false;
        this.cdr.detectChanges();
      } else if (versionType === 'comparing') {
        this.formatData(partnersData, 'comparing');
        this.comparingDataLoading = false;
        this.comparingLoading = false;
        this.cdr.detectChanges();
      } else {
        // Só formatar dados normais se não estiver em modo de comparação
        if (!this.comparingVersions) {
          this.formatData(partnersData);
          this.loading = false;
          this.cdr.detectChanges();
        } else {
          this.loading = false;
        }
      }
    } catch (error) {
      console.error('Erro ao buscar dados de composição acionária:', error);
      if (versionType === 'main') {
        this.mainData = [];
        this.mainLoading = false;
        this.cdr.detectChanges();
      } else if (versionType === 'comparing') {
        this.comparingData = [];
        this.comparingDataLoading = false;
        this.comparingLoading = false;
        this.cdr.detectChanges();
      } else {
        this.loading = false;
        this.cdr.detectChanges();
      }
    } finally {
      // Decrementar contador de loading v3 quando terminar requisição (apenas no modo normal, não comparação)
      if (!versionType && !this.comparingVersions) {
        this.approvalService.decrementV3Loading();
      }
    }
  }

  filterPartners(partners) {
    return partners.filter((partner: Partner) => partner.document);
  }

  closeModal() {
    this.compositionDetails = false;
  }

  openModal(item: Partner) {
    this.compositionDetails = true;
    this.selectedPartner = item;
  }

  handleSelect(idx: any) {
    this.file = this.selectedPartner.files[idx];
    this.getImgUrl();
  }

  formatData(data: any, versionType: 'main' | 'comparing' | null = null) {
    if (!data || !Array.isArray(data)) {
      return;
    }

    const firstObject: PartnerResponse = {
      id: undefined,
      percentage: 0,
      isOpen: true,
      pep: false,
      full_name: 'Grupo principal',
      document: '',
      parent: null,
      title: '',
      pep_bond: '',
      pep_relationship: false,
      pep_relationship_document: '',
      pep_relationship_name: '',
      shareholders: [],
      register: '',
      collapsed: false,
    };

    const rootObjects = [];

    data.forEach((item) => {
      if (item.parent === null) {
        firstObject.shareholders.push(item);
      }

      let doc = Utils.onlyNumbers(item.document);

      if (doc.length > 14) {
        rootObjects.push(item);
      }
    });

    const newData = data.map((item) => {
      return {
        ...item,
        collapsed: false,
        isOpen: true,
      };
    });

    const newFormattedData = [firstObject, ...newData];

    if (versionType === 'main') {
      this.mainData = newFormattedData;
    } else if (versionType === 'comparing') {
      this.comparingData = newFormattedData;
    } else {
      this.data = newFormattedData;
    }
  }

  getShareholdersPercentage(
    item_id?: number,
    versionType: 'main' | 'comparing' | null = null
  ) {
    try {
      const dataForView = this.getDataForView(versionType);
      if (!dataForView || dataForView.length === 0) {
        return 0;
      }

      let maxPercentage = 0;

      if (item_id !== undefined) {
        const partner = dataForView.find((item) => item && item.id === item_id);
        if (
          partner &&
          partner.shareholders &&
          Array.isArray(partner.shareholders)
        ) {
          maxPercentage = partner.shareholders.reduce((prev, curr) => {
            if (!curr || curr.percentage === undefined) return prev;
            return Number(prev.toFixed(4)) + Number(curr.percentage.toFixed(4));
          }, 0);
        }
      } else {
        const rootObjects = dataForView.filter(
          (item) => item && item.parent === null
        );
        maxPercentage = rootObjects.reduce((prev, curr) => {
          if (!curr || curr.percentage === undefined) return prev;
          return Number(prev.toFixed(4)) + Number(curr.percentage.toFixed(4));
        }, 0);
      }

      return Number(maxPercentage.toFixed(4));
    } catch (error) {
      return 0;
    }
  }

  // Métodos auxiliares para obter dados baseados no tipo de versão
  getDataForView(
    versionType: 'main' | 'comparing' | null = null
  ): PartnerResponse[] {
    if (!this.comparingVersions) {
      return this.data || [];
    }
    if (versionType === 'main') {
      return this.mainData || [];
    }
    if (versionType === 'comparing') {
      return this.comparingData || [];
    }
    return this.data || [];
  }

  // Método para incluir itens removidos no lado direito
  getDataForComparison(
    versionType: 'main' | 'comparing' | null
  ): PartnerResponse[] {
    if (!this.comparingVersions) {
      return this.data || [];
    }
    if (versionType === 'main') {
      return this.mainData || [];
    }
    if (versionType === 'comparing') {
      // Proteção: garantir que os arrays existam
      if (!this.mainData || !this.comparingData) {
        return this.comparingData || [];
      }

      // Criar um mapa dos itens do main por ID para verificar se existe
      const mainMap = new Map();
      this.mainData.forEach((item) => {
        if (item && item.id !== undefined) {
          mainMap.set(item.id, item);
        }
      });

      // Criar um mapa dos itens do comparing por ID para busca rápida
      const comparingMap = new Map();
      this.comparingData.forEach((item) => {
        if (item && item.id !== undefined) {
          comparingMap.set(item.id, item);
        }
      });

      // Reconstruir o array mantendo a ordem do mainData
      // Incluir itens removidos na posição correta
      const result: PartnerResponse[] = [];

      // Primeiro, adicionar o "Grupo principal" do comparing
      const grupoPrincipal = this.comparingData.find(
        (item) => item.id === undefined
      );
      if (grupoPrincipal) {
        result.push(grupoPrincipal);
      }

      // Depois, iterar sobre o mainData para manter a ordem original
      this.mainData.forEach((mainItem) => {
        // Ignorar o "Grupo principal" (já foi adicionado)
        if (mainItem.id === undefined) return;

        const comparingItem = comparingMap.get(mainItem.id);

        if (comparingItem) {
          // Item existe em ambos, adicionar do comparing
          result.push(comparingItem);
        } else {
          // Item removido (existe apenas no main), adicionar com propriedades garantidas
          result.push({
            ...mainItem,
            collapsed:
              mainItem.collapsed !== undefined ? mainItem.collapsed : false,
            isOpen: mainItem.isOpen !== undefined ? mainItem.isOpen : true,
          });
        }
      });

      // Adicionar itens que existem apenas no comparing (adicionados)
      this.comparingData.forEach((comparingItem) => {
        if (comparingItem.id !== undefined && !mainMap.has(comparingItem.id)) {
          // Verificar se já não foi adicionado
          if (!result.find((item) => item.id === comparingItem.id)) {
            result.push(comparingItem);
          }
        }
      });

      return result;
    }
    return this.data || [];
  }

  getLoadingForView(versionType: 'main' | 'comparing' | null = null): boolean {
    if (!this.comparingVersions) return this.loading;
    if (versionType === 'main') return this.mainLoading;
    if (versionType === 'comparing')
      return this.comparingDataLoading || this.comparingLoading;
    return this.loading;
  }

  // Métodos de comparação
  getItemStatus(
    item: PartnerResponse,
    versionType: 'main' | 'comparing' | null
  ): 'modified' | 'added' | 'removed' | 'unchanged' {
    try {
      if (!this.comparingVersions || !versionType || !item) return 'unchanged';

      // Ignorar o "Grupo principal" (id === undefined)
      if (item.id === undefined) return 'unchanged';

      const mainData = this.mainData || [];
      const comparingData = this.comparingData || [];

      if (mainData.length === 0 || comparingData.length === 0) {
        return 'unchanged';
      }

      const itemId = item.id;
      const mainItem = mainData.find((item) => item && item.id === itemId);
      const comparingItem = comparingData.find(
        (item) => item && item.id === itemId
      );

      if (!mainItem && comparingItem) {
        return versionType === 'comparing' ? 'added' : 'unchanged';
      }

      if (mainItem && !comparingItem) {
        return versionType === 'comparing' ? 'removed' : 'unchanged';
      }

      if (mainItem && comparingItem) {
        const isModified = this.isItemModified(mainItem, comparingItem);
        return isModified ? 'modified' : 'unchanged';
      }

      return 'unchanged';
    } catch (error) {
      return 'unchanged';
    }
  }

  isItemModified(
    mainItem: PartnerResponse,
    comparingItem: PartnerResponse
  ): boolean {
    try {
      if (!mainItem || !comparingItem) return false;

      // Comparar campos principais
      if (
        mainItem.full_name !== comparingItem.full_name ||
        mainItem.document !== comparingItem.document ||
        mainItem.percentage !== comparingItem.percentage ||
        mainItem.pep !== comparingItem.pep ||
        mainItem.pep_relationship !== comparingItem.pep_relationship ||
        mainItem.pep_relationship_name !==
          comparingItem.pep_relationship_name ||
        mainItem.pep_relationship_document !==
          comparingItem.pep_relationship_document ||
        mainItem.pep_bond !== comparingItem.pep_bond
      ) {
        return true;
      }

      // Comparar shareholders
      const mainShareholders = mainItem.shareholders || [];
      const comparingShareholders = comparingItem.shareholders || [];

      if (mainShareholders.length !== comparingShareholders.length) {
        return true;
      }

      for (let i = 0; i < mainShareholders.length; i++) {
        const mainShareholder = mainShareholders[i];
        if (!mainShareholder) continue;

        const comparingShareholder = comparingShareholders.find(
          (s) => s && s.id === mainShareholder.id
        );

        if (!comparingShareholder) {
          return true;
        }

        if (
          mainShareholder.full_name !== comparingShareholder.full_name ||
          mainShareholder.document !== comparingShareholder.document ||
          mainShareholder.percentage !== comparingShareholder.percentage
        ) {
          return true;
        }
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  getItemRowClass(
    item: PartnerResponse,
    versionType: 'main' | 'comparing' | null
  ): string {
    if (versionType !== 'comparing') return '';
    const status = this.getItemStatus(item, versionType);
    switch (status) {
      case 'modified':
        return 'row-compare-modified';
      case 'added':
        return 'row-compare-added';
      case 'removed':
        return 'row-compare-removed';
      default:
        return '';
    }
  }

  getItemIcon(
    item: PartnerResponse,
    versionType: 'main' | 'comparing' | null
  ): string {
    if (versionType !== 'comparing') return '';
    const status = this.getItemStatus(item, versionType);
    switch (status) {
      case 'added':
        return '<img src="/assets/images/carbon-checkmark.svg" class="comparison-icon" style="width: 16px; height: 16px;" alt="check" />';
      case 'removed':
        return '<img src="/assets/images/carbon-error-warning.svg" class="comparison-icon" style="width: 16px; height: 16px;" alt="error" />';
      default:
        return '';
    }
  }

  isColumnFieldModified(
    item: PartnerResponse,
    versionType: 'main' | 'comparing' | null,
    field:
      | 'full_name'
      | 'document'
      | 'percentage'
      | 'pep_relationship_name'
      | 'pep_bond'
  ): boolean {
    try {
      if (
        !this.comparingVersions ||
        !versionType ||
        versionType !== 'comparing' ||
        !item
      )
        return false;

      const mainData = this.mainData || [];
      const comparingData = this.comparingData || [];

      if (mainData.length === 0 || comparingData.length === 0) {
        return false;
      }

      const itemId = item.id;
      if (itemId === undefined) return false;

      const mainItem = mainData.find((item) => item && item.id === itemId);
      const comparingItem = comparingData.find(
        (item) => item && item.id === itemId
      );

      if (!mainItem || !comparingItem) return false;

      switch (field) {
        case 'full_name':
          return mainItem.full_name !== comparingItem.full_name;
        case 'document':
          return mainItem.document !== comparingItem.document;
        case 'percentage':
          return mainItem.percentage !== comparingItem.percentage;
        case 'pep_relationship_name':
          return (
            mainItem.pep_relationship_name !==
            comparingItem.pep_relationship_name
          );
        case 'pep_bond':
          return mainItem.pep_bond !== comparingItem.pep_bond;
        default:
          return false;
      }
    } catch (error) {
      return false;
    }
  }

  getFieldComparisonIcon(
    item: PartnerResponse,
    versionType: 'main' | 'comparing' | null,
    field:
      | 'full_name'
      | 'document'
      | 'percentage'
      | 'pep_relationship_name'
      | 'pep_bond'
  ): string {
    try {
      if (
        !this.comparingVersions ||
        !versionType ||
        versionType !== 'comparing' ||
        !item
      )
        return '';

      const isModified = this.isColumnFieldModified(item, versionType, field);
      if (isModified) {
        return '<img src="/assets/images/carbon-warning.svg" class="comparison-icon" style="width: 16px; height: 16px;" alt="warning" />';
      }

      return '';
    } catch (error) {
      return '';
    }
  }

  handleSetCollapsed(
    index: number,
    isOpen: boolean,
    versionType: 'main' | 'comparing' | null = null
  ) {
    try {
      const dataForView = this.getDataForView(versionType);

      if (!dataForView || !dataForView[index]) {
        return;
      }

      if (!isOpen) {
        const shareholders = dataForView[index].shareholders || [];
        shareholders.forEach((item) => {
          if (!item || item.id === undefined) return;
          const shareholderIndex = dataForView.findIndex(
            (i) => i && i.id === item.id
          );
          if (shareholderIndex >= 0 && dataForView[shareholderIndex]) {
            dataForView[shareholderIndex].collapsed = false;
          }
        });

        dataForView[index].isOpen = true;
        return;
      }

      dataForView[index].isOpen = false;

      if (isOpen) {
        const shareholders = dataForView[index].shareholders || [];
        shareholders.forEach((item) => {
          if (!item || item.id === undefined) return;
          const shareholderIndex = dataForView.findIndex(
            (i) => i && i.id === item.id
          );
          if (shareholderIndex >= 0 && dataForView[shareholderIndex]) {
            dataForView[shareholderIndex].collapsed = true;
            this.handleSetCollapsed(shareholderIndex, true, versionType);
          }
        });
      }
    } catch (error) {
      // Erro silencioso
    }
  }

  getSpace(index: number, versionType: 'main' | 'comparing' | null = null) {
    const multiplyNumber = this.getMultiplier(index, versionType);

    return multiplyNumber * 32;
  }

  getMultiplier(
    index: number,
    versionType: 'main' | 'comparing' | null = null
  ) {
    try {
      const dataForView = this.getDataForView(versionType);

      if (!dataForView || !dataForView[index]) {
        return 0;
      }

      let multiplyNumber = dataForView[index].id ? 1 : 0;
      let haveParent = dataForView[index].parent !== null;
      let parentId = 0;
      let findedIndex = index;

      while (haveParent) {
        multiplyNumber++;
        parentId = dataForView[findedIndex].parent;

        if (parentId === null) {
          haveParent = false;
          break;
        }

        findedIndex = dataForView.findIndex(
          (item) => item && item.id === parentId
        );

        if (findedIndex === -1 || !dataForView[findedIndex]) {
          haveParent = false;
          break;
        }

        haveParent = dataForView[findedIndex].parent !== null;
      }

      return multiplyNumber;
    } catch (error) {
      return 0;
    }
  }

  getImgUrl() {
    const regex = /\.([0-9a-z]+)(?:[\?#]|$)/i;

    const resultado = regex.exec(this.file.file);

    if (resultado && resultado[1]) {
      const extension = resultado[1].toLowerCase();

      switch (extension) {
        case 'pdf':
          this.isPDF = true;
          break;
        case 'xls':
        case 'xlsx':
          this.isXLS = true;
          break;
        default:
          this.isPDF = false;
          this.isXLS = false;
          break;
      }
    }

    let url: any = this.file.file;

    if (this.isPDF) {
      url = this.sanitizer.bypassSecurityTrustResourceUrl(this.file.file);
    }

    if (this.isXLS) {
      url = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://view.officeapps.live.com/op/embed.aspx?src=${this.file.file}`
      );
    }

    this.url = url;

    this.modalRoot.show();
  }

  formatDocument(document: string): string {
    if (!document) return '';

    const onlyNumbers = Utils.onlyNumbers(document);

    if (onlyNumbers.length === 11) {
      // CPF
      return this.maskPipe.transform(onlyNumbers, '000.000.000-00');
    } else if (onlyNumbers.length === 14) {
      // CNPJ
      return this.maskPipe.transform(onlyNumbers, '00.000.000/0000-00');
    }

    return document;
  }

  /**
   * Verifica se há dados válidos de composição acionária
   * Retorna true se houver acionistas além do "Grupo principal"
   */
  hasCompositionData(data: PartnerResponse[] | null): boolean {
    if (!data || data.length === 0) {
      return false;
    }

    // Se só tem o "Grupo principal" e ele não tem shareholders, não há dados
    if (data.length === 1) {
      const grupoPrincipal = data[0];
      if (!grupoPrincipal || grupoPrincipal.id === undefined) {
        // É o grupo principal, verificar se tem shareholders
        return grupoPrincipal?.shareholders && grupoPrincipal.shareholders.length > 0;
      }
    }

    // Se tem mais de 1 item, há dados (pelo menos um acionista além do grupo principal)
    return data.length > 1;
  }
}
